// member-portal/src/App.js — FIXED: Promoters auto-redirect to Refer & Earn + hide normal nav
// All original functionality preserved — only added promoter-specific logic

import React, { useState, useEffect, useCallback } from 'react';
import CookieConsent from 'react-cookie-consent';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';
import Landing from './components/Landing';
import AuthForms from './components/AuthForms';
import Dashboard from './components/Dashboard';
import Deals from './components/Deals';
import DealDetail from './components/DealDetail';
import DealForm from './components/DealForm';
import EditProfile from './components/EditProfile';
import OrganizationForm from './components/OrganizationForm';
import UsersManagement from './components/UsersManagement';
import Report from './components/Report';
import Banner from './components/Banner';
import SuperAdminPortal from './components/SuperAdminPortal';
import VerificationPending from './components/VerificationPending';
import Verify from './components/Verify';
import InvoiceEditor from './components/InvoiceEditor';
import MemberBilling from './components/MemberBilling';
import SessionExpiredModal from './components/SessionExpiredModal';
import ErrorBoundary from './components/ErrorBoundary';
import ResetPassword from './components/ResetPassword';
import PromoterDashboard from './components/PromoterDashboard'; // ← Promoter dashboard
import PromoterSignup from './components/PromoterSignup'; // ← NEW: Promoter signup view
import useAuth from './hooks/useAuth'; // ← RESTORED
import useDeals from './hooks/useDeals'; // ← RESTORED
import setupAxiosInterceptors from './axiosConfig'; // ← RESTORED
import logo from './logo.png'; // ← RESTORED

const App = () => {
  const [view, setView] = useState('landing');
  const [history, setHistory] = useState([]);
  const [initialPath, setInitialPath] = useState('');
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const auth = useAuth(setView);
  const { user, setUser, handleLogout, onSessionExpired, showTimeoutWarning, handleStayLoggedIn, handleInactivityLogout } = auth;
  const dealHook = useDeals(user, onSessionExpired);
  const { deals, fetchDeals, handleDealRedirect } = dealHook;

  const navigateTo = useCallback((newView, params = {}) => {
    console.log(`Navigating to ${newView} with params:`, params);
    setHistory(prevHistory => [...prevHistory, { view, params }]);
    setView(newView);
  }, [view]);

  const goBack = useCallback(() => {
    if (history.length > 0) {
      const previous = history[history.length - 1];
      console.log(`Going back to ${previous.view}`);
      setView(previous.view);
      setHistory(history.slice(0, -1));
    } else {
      const defaultView = user && user.email === 'admin@scover.co.za' && user.role === 'SuperAdmin' ? 'superAdmin' :
                          user && user.organization?.verificationStatus !== 'approved' ? 'verificationPending' : 'landing';
      console.log(`No history, going to default view: ${defaultView}`);
      setView(defaultView);
    }
  }, [history, user]);

  const handleSessionExpired = useCallback(() => {
    if (!isSessionExpired) {
      console.log('Handling session expired');
      setIsSessionExpired(true);
      handleLogout();
    }
  }, [isSessionExpired, handleLogout]);

  const handleLoginRedirect = useCallback(() => {
    console.log('Redirecting to login');
    setIsSessionExpired(false);
    setView('login');
  }, [setView]);

  useEffect(() => {
    setupAxiosInterceptors(handleSessionExpired);
  }, [handleSessionExpired]);

  useEffect(() => {
    console.log('Initial render, setting view based on URL');
    const params = new URLSearchParams(window.location.search);
    const path = window.location.pathname;
    setInitialPath(path);

    if (path.startsWith('/verify/')) {
      const orgId = path.split('/verify/')[1];
      setView('verify');
      setHistory([{ view: 'landing', params: { organizationId: orgId } }]);
    } else if (path.startsWith('/invoice-editor/')) {
      const invoiceId = path.split('/invoice-editor/')[1];
      setView('invoiceEditor');
      setHistory([{ view: 'superAdmin', params: { invoiceId } }]);
    } else if (path.startsWith('/reset-password')) {
      setView('reset-password');
    } else {
      setView('landing');
      setUser(null);
    }
  }, [setView, setUser]);

  const renderView = () => {
    if (view === 'invoiceEditor') {
      const invoiceId = history[history.length - 1]?.params?.invoiceId;
      if (invoiceId) {
        return <InvoiceEditor invoiceId={invoiceId} onClose={goBack} />;
      }
      return <div className="error-container">Invalid invoice</div>;
    }

    if (!user) {
      console.log(`No user, rendering view: ${view}`);
      switch (view) {
        case 'login':
          return <AuthForms type="login" auth={auth} setView={setView} />;
        case 'signup':
          return <AuthForms type="signup" auth={auth} setView={setView} />;
        case 'verify':
          return history.length > 0 ? (
            <Verify organizationId={history[history.length - 1].params.organizationId} setView={setView} />
          ) : (
            <div className="error-container">
              <p>Invalid verification link</p>
              <button onClick={() => setView('landing')}>Back to Home</button>
            </div>
          );
        case 'reset-password':
          return <ResetPassword setView={setView} />;

        case 'promoter-signup':
          return <PromoterSignup setView={setView} />;

        default:
          return <Landing navigateTo={navigateTo} logo={logo} />;
      }
    }

    if (user?.email === 'admin@scover.co.za' && user?.role === 'SuperAdmin') {
      console.log(`SuperAdmin user, rendering view: ${view}`);
      switch (view) {
        case 'superAdmin':
          return <SuperAdminPortal user={user} navigateTo={navigateTo} goBack={goBack} onSessionExpired={handleSessionExpired} />;
        case 'dealDetail':
          return history.length > 0 ? (
            <DealDetail
              deal={history[history.length - 1].params.deal}
              user={user}
              goBack={goBack}
              handleDealRedirect={handleDealRedirect}
              navigateTo={navigateTo}
              onSessionExpired={handleSessionExpired}
            />
          ) : (
            <div className="super-admin-redirect">
              <p>Invalid deal detail navigation. Returning to SuperAdmin Portal...</p>
              <button onClick={() => navigateTo('superAdmin')}>Go to SuperAdmin Portal</button>
            </div>
          );
        case 'dealForm':
          return (
            <DealForm
              user={user}
              goBack={goBack}
              fetchDeals={fetchDeals}
              deal={history[history.length - 1]?.params?.deal}
              isEdit={history[history.length - 1]?.params?.isEdit}
            />
          );
        case 'organization':
          return <OrganizationForm user={user} goBack={goBack} org={history[history.length - 1]?.params?.org} />;
        default:
          return <SuperAdminPortal user={user} navigateTo={navigateTo} goBack={goBack} onSessionExpired={handleSessionExpired} />;
      }
    }

    // ─────────────────────────────────────────────────────────────
    // FIXED: Bypass VerificationPending for Promoters
    // Promoters have no organization, so skip this check
    // Added ~3 lines – fully reversible by commenting out this block
    // ─────────────────────────────────────────────────────────────
    if (user.role !== 'Promoter' && user?.organization?.verificationStatus !== 'approved') {
      console.log(`Unverified organization, rendering view: ${view}`);
      return <VerificationPending user={user} handleLogout={handleLogout} />;
    }
    // ─────────────────────────────────────────────────────────────

    console.log(`Verified user, rendering view: ${view}`);

    // ─────────────────────────────────────────────────────────────
    // FIXED: Promoters get their own full-page dashboard (no normal nav)
    // Normal users get regular dashboard + nav
    // Added ~8 lines – fully reversible by commenting out this block
    // ─────────────────────────────────────────────────────────────
    if (user.role === 'Promoter') {
      switch (view) {
        case 'promoters':
          return <PromoterDashboard user={user} navigateTo={navigateTo} />;
        default:
          // Force promoters to promoters view if they try anything else
          setView('promoters');
          return <PromoterDashboard user={user} navigateTo={navigateTo} />;
      }
    }
    // ─────────────────────────────────────────────────────────────

    // Normal member flow (with NavButtons)
    switch (view) {
      case 'dashboard':
        return <Dashboard navigateTo={navigateTo} deals={deals} user={user} />;
      case 'deals':
        return (
          <Deals
            deals={deals}
            fetchDeals={fetchDeals}
            navigateTo={navigateTo}
            goBack={goBack}
            handleDealRedirect={handleDealRedirect}
            user={user}
            onSessionExpired={handleSessionExpired}
          />
        );
      case 'dealDetail':
        return history.length > 0 ? (
          <DealDetail
            deal={history[history.length - 1].params.deal}
            user={user}
            goBack={goBack}
            handleDealRedirect={handleDealRedirect}
            navigateTo={navigateTo}
            onSessionExpired={handleSessionExpired}
          />
        ) : null;
      case 'dealForm':
        return (
          <DealForm
            user={user}
            goBack={goBack}
            fetchDeals={fetchDeals}
            deal={history[history.length - 1]?.params?.deal}
            isEdit={history[history.length - 1]?.params?.isEdit}
          />
        );
      case 'editProfile':
        return <EditProfile auth={auth} goBack={goBack} />;
      case 'organization':
        return <OrganizationForm user={user} goBack={goBack} />;
      case 'users':
        return <UsersManagement user={user} navigateTo={navigateTo} />;
      case 'report':
        return <Report user={user} navigateTo={navigateTo} goBack={goBack} onSessionExpired={handleSessionExpired} />;
      case 'billing':
        return <MemberBilling navigateTo={navigateTo} user={user} />;

      default:
        return <Dashboard navigateTo={navigateTo} deals={deals} user={user} />;
    }
  };

  useEffect(() => {
    console.log('Checking modal render, isSessionExpired:', isSessionExpired, 'showTimeoutWarning:', showTimeoutWarning);
  }, [isSessionExpired, showTimeoutWarning]);

  return (
    <ErrorBoundary>
      <div className="app">
        <ToastContainer position="top-right" autoClose={3000} />
        <CookieConsent
          location="bottom"
          buttonText="Accept"
          declineButtonText="Reject"
          cookieName="scoverCookieConsentMemberPortal"
          style={{ background: "#2B373B", color: "#fff", fontSize: "14px", padding: "10px 20px" }}
          buttonStyle={{ background: "#0066cc", color: "#fff", fontSize: "13px", padding: "5px 10px", borderRadius: "3px" }}
          declineButtonStyle={{ background: "#ccc", color: "#333", fontSize: "13px", padding: "5px 10px", borderRadius: "3px" }}
          enableDeclineButton
          onAccept={() => {
            if (window.gtag) {
              window.gtag('consent', 'update', { analytics_storage: 'granted' });
            }
          }}
          onDecline={() => {
            if (window.gtag) {
              window.gtag('consent', 'update', { analytics_storage: 'denied' });
            }
          }}
        >
          <>
            We use cookies to enhance your experience, enable functionality, and analyze usage. By clicking "Accept", you agree to our{' '}
            <a href="https://scover.co.za/legal#privacy" style={{ color: '#fff', textDecoration: 'underline' }}>
              Privacy Policy
            </a>.
          </>
        </CookieConsent>
        <Banner
          user={user}
          setView={setView}
          handleLogout={handleLogout}
          navigateTo={navigateTo}
          isLanding={view === 'landing'}
        />
        {renderView()}
        {(isSessionExpired || showTimeoutWarning) && (
          <SessionExpiredModal
            onLogin={showTimeoutWarning ? handleInactivityLogout : handleLoginRedirect}
            isTimeoutWarning={showTimeoutWarning}
            onStayLoggedIn={handleStayLoggedIn}
          />
        )}
      </div>
    </ErrorBoundary>
  );
};

export default App;